import { FetchPolicy } from "@apollo/client";
import { useGetAllUsersQuery, UserDataFragment } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { MagnifyingGlassIcon, MultiGroupedSelect, MultiSelect } from "@bonliva-crm/ui";
import { Box, makeStyles } from "@material-ui/core";
import { createGroupedUserOptions } from "./utils/create-grouped-user-options";
import { useUserProfile } from "@hooks";
import { createUserOptions } from "./utils/create-user-options";
import { useMemo } from "react";

type Option = {
  label: string;
  value: UserDataFragment;
};

type Props<T extends boolean = true> = {
  fetchPolicy?: FetchPolicy;
  ignoreMe?: boolean;
  groupedByTeam?: T;
  prependUsers?: UserDataFragment[];
  value: UserDataFragment[];
  onChange: (users: UserDataFragment[]) => void;
  selectProps?: T extends true
    ? React.ComponentProps<typeof MultiGroupedSelect<Option>>
    : React.ComponentProps<typeof MultiSelect<Option>>;
};

export function UserSearchMulti<T extends boolean = true>({
  ignoreMe = true,
  groupedByTeam = true as T,
  ...props
}: Props<T>) {
  const classes = useStyles();
  const user = useUserProfile();
  const users = useGetAllUsersQuery({
    fetchPolicy: props.fetchPolicy ?? "cache-first"
  });

  const inputOptions = useMemo(() => {
    if (!users.data) {
      return [];
    }

    const options = props.prependUsers
      ? [...props.prependUsers, ...users.data.users]
      : users.data.users;

    const filteredUsers = ignoreMe ? options.filter((u) => u._id !== user.id) : options;

    return groupedByTeam
      ? createGroupedUserOptions(filteredUsers)
      : createUserOptions(filteredUsers);
  }, [ignoreMe, groupedByTeam, users.data, user, props.prependUsers]);

  const selectedOptions = (
    ignoreMe ? props.value.filter((u) => u._id !== user.id) : props.value
  ).map((user) => ({
    label: user.Name,
    value: user
  }));

  return groupedByTeam ? (
    <MultiGroupedSelect<Option>
      placeholder={
        <Box className={classes.placeholder}>
          <MagnifyingGlassIcon />
          {locale.Search}...
        </Box>
      }
      isMulti={true}
      options={inputOptions}
      value={selectedOptions}
      showDropDownIndicator={true}
      isClearable={false}
      maxMenuHeight={2000}
      controlShouldRenderValue={true}
      onChange={(options) => props.onChange(options.map((option) => option.value))}
      styleProps={{ menu: { showBorder: true }, control: { showBorder: true } }}
      closeMenuOnSelect={false}
      hidePlaceholderOnFocus
      {...props.selectProps}
    />
  ) : (
    <MultiSelect<Option>
      placeholder={
        <Box className={classes.placeholder}>
          <MagnifyingGlassIcon />
          {locale.Search}...
        </Box>
      }
      isMulti={true}
      options={inputOptions}
      value={selectedOptions}
      showDropDownIndicator={true}
      isClearable={false}
      maxMenuHeight={2000}
      controlShouldRenderValue={true}
      onChange={(options) => props.onChange(options.map((option) => option.value))}
      styleProps={{ menu: { showBorder: true }, control: { showBorder: true } }}
      closeMenuOnSelect={false}
      hidePlaceholderOnFocus
      {...props.selectProps}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  placeholder: {
    display: "flex",
    columnGap: "0.5rem",
    alignItems: "center"
  }
}));
